import { Point } from '../core/types';

/**
 * Shape rasterisation in an abstract integer grid.
 *
 * These primitives are shared by the cell tools and the Braille tools — the
 * only difference is the resolution of the grid they plot into — so they take a
 * plot callback rather than touching any document state.
 */

export type Plot = (x: number, y: number) => void;

export function plotLine(x0: number, y0: number, x1: number, y1: number, plot: Plot): void {
  let x = Math.round(x0);
  let y = Math.round(y0);
  const ex = Math.round(x1);
  const ey = Math.round(y1);
  const dx = Math.abs(ex - x);
  const dy = -Math.abs(ey - y);
  const sx = x < ex ? 1 : -1;
  const sy = y < ey ? 1 : -1;
  let err = dx + dy;
  for (;;) {
    plot(x, y);
    if (x === ex && y === ey) return;
    const e2 = 2 * err;
    if (e2 >= dy) {
      err += dy;
      x += sx;
    }
    if (e2 <= dx) {
      err += dx;
      y += sy;
    }
  }
}

export function plotRect(
  x: number,
  y: number,
  width: number,
  height: number,
  plot: Plot,
  filled = false,
): void {
  if (width <= 0 || height <= 0) return;
  const x2 = x + width - 1;
  const y2 = y + height - 1;
  if (filled) {
    for (let cy = y; cy <= y2; cy++) {
      for (let cx = x; cx <= x2; cx++) plot(cx, cy);
    }
    return;
  }
  for (let cx = x; cx <= x2; cx++) {
    plot(cx, y);
    plot(cx, y2);
  }
  for (let cy = y; cy <= y2; cy++) {
    plot(x, cy);
    plot(x2, cy);
  }
}

/**
 * Midpoint ellipse inscribed in the given bounding box.
 *
 * Terminal cells are roughly twice as tall as they are wide, so callers that
 * want a *visually* round circle pass an aspect-corrected box; the rasteriser
 * itself works in plain grid units.
 */
export function plotEllipse(
  x: number,
  y: number,
  width: number,
  height: number,
  plot: Plot,
  filled = false,
): void {
  if (width <= 0 || height <= 0) return;
  if (width <= 2 || height <= 2) {
    plotRect(x, y, width, height, plot, filled);
    return;
  }
  const a = (width - 1) / 2;
  const b = (height - 1) / 2;
  const cx = x + a;
  const cy = y + b;

  const spans = new Map<number, [number, number]>();
  const mark = (px: number, py: number) => {
    if (filled) {
      const span = spans.get(py);
      if (!span) spans.set(py, [px, px]);
      else {
        if (px < span[0]) span[0] = px;
        if (px > span[1]) span[1] = px;
      }
    } else {
      plot(px, py);
    }
  };

  // Sample by angle: robust for the small, highly non-square boxes that
  // terminal shapes produce, where integer midpoint stepping leaves gaps.
  const steps = Math.max(32, Math.ceil((width + height) * 4));
  for (let i = 0; i < steps; i++) {
    const t = (i / steps) * Math.PI * 2;
    mark(Math.round(cx + a * Math.cos(t)), Math.round(cy + b * Math.sin(t)));
  }
  if (filled) {
    for (const [py, [x0, x1]] of spans) {
      for (let px = x0; px <= x1; px++) plot(px, py);
    }
  }
}

/** Polyline through a freehand point list, gap-filled with straight segments. */
export function plotPath(points: readonly Point[], plot: Plot): void {
  if (points.length === 0) return;
  if (points.length === 1) {
    plot(points[0].x, points[0].y);
    return;
  }
  for (let i = 1; i < points.length; i++) {
    plotLine(points[i - 1].x, points[i - 1].y, points[i].x, points[i].y, plot);
  }
}

/** Square brush footprint centred on a point. */
export function plotBrush(x: number, y: number, size: number, plot: Plot): void {
  if (size <= 1) {
    plot(x, y);
    return;
  }
  const half = Math.floor(size / 2);
  for (let dy = -half; dy <= half; dy++) {
    for (let dx = -half; dx <= half; dx++) plot(x + dx, y + dy);
  }
}

/**
 * 4-connected flood fill.
 *
 * `match` decides what counts as "the same region" — the fill tool passes a
 * comparison over char/fg/bg so that filling an area of one colour stops at a
 * border drawn in another.
 */
export function floodFill(
  width: number,
  height: number,
  startX: number,
  startY: number,
  match: (x: number, y: number) => boolean,
  plot: Plot,
  limit = 1_000_000,
): number {
  if (startX < 0 || startY < 0 || startX >= width || startY >= height) return 0;
  if (!match(startX, startY)) return 0;
  const seen = new Uint8Array(width * height);
  const stack: number[] = [startY * width + startX];
  let filled = 0;
  while (stack.length > 0 && filled < limit) {
    const i = stack.pop() as number;
    if (seen[i]) continue;
    seen[i] = 1;
    const x = i % width;
    const y = (i - x) / width;
    if (!match(x, y)) continue;
    plot(x, y);
    filled += 1;
    if (x > 0) stack.push(i - 1);
    if (x < width - 1) stack.push(i + 1);
    if (y > 0) stack.push(i - width);
    if (y < height - 1) stack.push(i + width);
  }
  return filled;
}

/** Arrowhead glyph for a direction, used by the arrow tool. */
export function arrowHead(dx: number, dy: number): string {
  if (Math.abs(dx) >= Math.abs(dy)) return dx >= 0 ? '▶' : '◀';
  return dy >= 0 ? '▼' : '▲';
}
